"use client";

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { callMcpAppTool, loadMcpAppResource } from "@/lib/mcpApps/client";
import {
  createHostBridge,
  hostStyleVariables,
  type JsonRpcMessage,
} from "@/lib/mcpApps/hostBridge";
import type { McpAppPayload, McpAppUri } from "@/lib/mcpApps/payload";
import { buildSrcDoc } from "@/lib/mcpApps/srcdoc";

const TITLES: Record<McpAppUri, string> = {
  "ui://gondola/hotel-search/v1": "Hotel search",
  "ui://gondola/hotel-details/v1": "Hotel details",
  "ui://gondola/compare-rates/v1": "Rate comparison",
  "ui://gondola/booking-receipt/v1": "Booking receipt",
};
// Views that never report their size keep this height.
const DEFAULT_HEIGHT = 420;

/**
 * Renders a Gondola MCP App view for one concierge tool call (GON-7817). The view runs in an
 * opaque-origin sandbox (scripts only) with its own CSP, and talks to the page only through the
 * host bridge. Any failure to load the view shows `fallback` instead.
 */
export default function McpAppFrame({
  payload,
  toolArguments,
  busy,
  onSendMessage,
  fallback,
}: {
  payload: McpAppPayload;
  toolArguments?: Record<string, unknown>;
  busy: boolean;
  onSendMessage?: (text: string) => void;
  fallback: ReactNode;
}) {
  const [doc, setDoc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [height, setHeight] = useState(DEFAULT_HEIGHT);
  const frameRef = useRef<HTMLIFrameElement>(null);
  // The bridge lives as long as the loaded document; it reads the latest props through these.
  const latest = useRef({ payload, toolArguments, busy, onSendMessage });
  useLayoutEffect(() => {
    latest.current = { payload, toolArguments, busy, onSendMessage };
  });

  useEffect(() => {
    let active = true;
    loadMcpAppResource(payload.resourceUri).then(
      (resource) => {
        if (active) setDoc(buildSrcDoc(resource.text, resource.meta));
      },
      () => {
        if (active) setFailed(true);
      }
    );
    return () => {
      active = false;
    };
  }, [payload.resourceUri]);

  // A layout effect, so the listener exists before the srcdoc document can post ui/initialize.
  useLayoutEffect(() => {
    if (!doc) return;
    const target = {
      postMessage: (message: JsonRpcMessage, origin: string) =>
        frameRef.current?.contentWindow?.postMessage(message, origin),
    };
    const root = getComputedStyle(document.documentElement);
    const bridge = createHostBridge({
      target,
      payload: latest.current.payload,
      toolArguments: latest.current.toolArguments,
      callTool: callMcpAppTool,
      onSendMessage: latest.current.onSendMessage
        ? (text) => latest.current.onSendMessage?.(text)
        : undefined,
      onHeight: setHeight,
      isBusy: () => latest.current.busy,
      styleVariables: hostStyleVariables((name) => root.getPropertyValue(name)),
    });
    const listener = (event: MessageEvent) => {
      const view = frameRef.current?.contentWindow;
      if (!view || event.source !== view) return;
      bridge.handleMessage({ source: target, data: event.data });
    };
    window.addEventListener("message", listener);
    return () => {
      window.removeEventListener("message", listener);
      bridge.teardown();
    };
  }, [doc]);

  if (failed) return <>{fallback}</>;
  if (!doc)
    return (
      <div data-testid="mcp-app-loading" aria-busy="true">
        <Skeleton className="w-full rounded-lg" height={DEFAULT_HEIGHT} />
      </div>
    );
  return (
    <iframe
      ref={frameRef}
      title={TITLES[payload.resourceUri]}
      sandbox="allow-scripts"
      srcDoc={doc}
      referrerPolicy="no-referrer"
      className="w-full rounded-lg border border-border bg-card"
      style={{ height }}
    />
  );
}
